/**
 * EventCard.tsx
 * Compact event card for browse page horizontal scroll + venue detail page.
 */
import React from 'react'
import { useNavigate } from 'react-router-dom'
import type { VenueEvent } from '../services/venueEventService'
import { openExternal } from '../utils/openExternal'
import { track } from '../services/analytics'

function fmtEventDate(dateStr: string, timeStr: string): string {
  const date = new Date(`${dateStr}T${timeStr}:00`)
  const today = new Date()
  const tomorrow = new Date(today)
  tomorrow.setDate(tomorrow.getDate() + 1)

  const isToday = dateStr === today.toISOString().split('T')[0]
  const isTomorrow = dateStr === tomorrow.toISOString().split('T')[0]

  const dayLabel = isToday
    ? 'Tonight'
    : isTomorrow
      ? 'Tomorrow'
      : date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })

  const time = date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true })
  return `${dayLabel} · ${time}`
}

interface EventCardProps {
  event: VenueEvent
  compact?: boolean  // for horizontal scroll row
}

export function EventCard({ event, compact = false }: EventCardProps) {
  const navigate = useNavigate()

  function handleVenueClick(e: React.MouseEvent) {
    e.stopPropagation()
    navigate(`/venue/${event.venue_id}`)
  }

  function handleTickets(e: React.MouseEvent) {
    e.stopPropagation()
    if (event.ticket_url) {
      openExternal(event.ticket_url)
      track('event_tickets_clicked', { event_id: event.id, event_title: event.title })
    }
  }

  if (compact) {
    return (
      <div className="ec-compact" onClick={() => navigate(`/venue/${event.venue_id}`)}>
        {/* Image or gradient placeholder */}
        {event.image_url ? (
          <img src={event.image_url} alt={event.title} className="ec-compact-img" />
        ) : (
          <div className="ec-compact-img-placeholder">
            <span className="ec-compact-emoji">🎉</span>
          </div>
        )}

        <div className="ec-compact-body">
          {event.is_featured && (
            <span className="ec-badge ec-badge--featured">⭐ Featured</span>
          )}
          <div className="ec-compact-title">{event.title}</div>
          <button
            className="ec-compact-venue"
            onClick={handleVenueClick}
          >
            {event.venue_name}
            {event.venue_neighborhood ? ` · ${event.venue_neighborhood}` : ''}
          </button>
          <div className="ec-compact-date">
            {fmtEventDate(event.event_date, event.start_time)}
          </div>
          <div className="ec-compact-price">
            {event.is_free
              ? <span className="ec-free">Free</span>
              : <span className="ec-paid">{event.price_text ?? 'Ticketed'}</span>
            }
          </div>
          {event.ticket_url && (
            <button className="ec-tickets-btn" onClick={handleTickets}>
              Get tickets →
            </button>
          )}
        </div>
      </div>
    )
  }

  // Full card (for venue detail page)
  return (
    <div className="ec-full">
      {event.image_url && (
        <img src={event.image_url} alt={event.title} className="ec-full-img" />
      )}
      <div className="ec-full-body">
        <div className="ec-full-header">
          <div>
            <div className="ec-full-title">{event.title}</div>
            <div className="ec-full-date">
              {fmtEventDate(event.event_date, event.start_time)}
              {event.end_time && ` – ${new Date(`${event.event_date}T${event.end_time}:00`)
                .toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true })}`}
            </div>
          </div>
          <div className="ec-full-price-block">
            {event.is_free
              ? <span className="ec-free">Free</span>
              : <span className="ec-paid">{event.price_text ?? 'Ticketed'}</span>
            }
          </div>
        </div>
        {event.description && (
          <p className="ec-full-desc">{event.description}</p>
        )}
        {event.ticket_url && (
          <button className="ec-tickets-btn ec-tickets-btn--full" onClick={handleTickets}>
            Get tickets →
          </button>
        )}
      </div>
    </div>
  )
}

// ── EVENTS SECTION (browse page horizontal row) ──────────────────────────────

interface EventsSectionProps {
  events: VenueEvent[]
}

export function EventsSection({ events }: EventsSectionProps) {
  if (events.length === 0) return null

  return (
    <div className="events-section">
      <div className="events-section-header">
        <div className="events-section-title-row">
          <span className="events-section-icon">🎉</span>
          <div>
            <h2 className="events-section-title">Events This Week</h2>
            <p className="events-section-sub">Special nights at Cincinnati bars</p>
          </div>
        </div>
      </div>
      <div className="events-scroll">
        {events.map(event => (
          <EventCard key={event.id} event={event} compact />
        ))}
      </div>
    </div>
  )
}
